import { Button } from "@/components/ui/Button";
import { SlideablePanel } from "@/components/ui/SlideablePanel";
import { useSessionStatus } from "@/features/chat/api/sessions";
import { useIsSessionStreaming } from "@/features/chat/streams/streamSelectors";
import { queryKeys } from "@/features/chat/types";
import { Card, CardContent } from "@/components/ui/Card";
import {
  downloadExtractionCsv,
  downloadExtractionCheckpointCsv,
  downloadExtractionCheckpointImages,
  downloadExtractionImages,
  ExtractionRequestError,
} from "@/features/data-curation/api/curation";
import { AddColumnDialog } from "@/features/data-curation/components/AddColumnDialog";
import { EditColumnDialog } from "@/features/data-curation/components/EditColumnDialog";
import {
  retryMessage,
  type CellAction,
  type SelectedGridCell,
} from "@/features/data-curation/cellSelection";
import {
  CurationChatPanel,
  CurationChatPlaceholder,
  type ChatComposerHandle,
  type ChatRequest,
} from "@/features/data-curation/components/CurationChatPanel";
import { CurationComposer } from "@/features/data-curation/components/CurationComposer";
import { FastDetailsPanel } from "@/features/data-curation/components/FastDetailsPanel";
import { FastQueryLine } from "@/features/data-curation/components/FastQueryLine";
import { CurationHeader } from "@/features/data-curation/components/CurationHeader";
import { CurationIncoming } from "@/features/data-curation/components/CurationIncoming";
import { CurationRowLimitBanner } from "@/features/data-curation/components/CurationRowLimitBanner";
import { GridFilters } from "@/features/data-curation/components/GridFilters";
import { filterGridRows } from "@/features/data-curation/gridFilter";
import { OldDatasetsBanner } from "@/features/data-curation/components/OldDatasetsBanner";
import { MonitorDialog } from "@/features/data-curation/components/MonitorDialog";
import { MonitorsPanel } from "@/features/data-curation/components/MonitorsPanel";
import { ProposedSchemaCard } from "@/features/data-curation/components/ProposedSchemaCard";
import { StartCollectionBar } from "@/features/data-curation/components/StartCollectionBar";
import { ComprehensiveProgress } from "@/features/data-curation/progress/components/ComprehensiveProgress";
import { useDatasetBuilderProgress } from "@/features/data-curation/progress/hooks";
import { fullReadOf } from "@/features/data-curation/progress/stages";
import { cn } from "@/utils/cn";
import { CurationTable } from "@/features/data-curation/components/CurationTable";
import { CurationWorkspaceLayout } from "@/features/data-curation/components/CurationWorkspaceLayout";
import {
  CURATION_CHAT_MAX_WIDTH,
  CURATION_CHAT_MIN_WIDTH,
  useCurationChatPanel,
} from "@/features/data-curation/hooks/useCurationChatPanel";
import {
  useAddGridChatNote,
  useClearExtractionCells,
  useDeleteExtractionDataset,
  useCreateExtractionCheckpoint,
  useDeliverChatNotes,
  useExtractionDataset,
  useExtractionDatasets,
  useExtractionCheckpoints,
  useExtractionGrid,
  useExtractionInspect,
  useExtractionRows,
  usePrefetchExtractionRows,
  usePrefetchRecentDatasets,
  useRestoreExtractionCheckpoint,
} from "@/features/data-curation/hooks/useCuration";
import { useRefreshDatasetMonitors } from "@/features/data-curation/hooks/useDatasetMonitors";
import type { MonitorScope } from "@/features/data-curation/monitors";
import {
  useDeletePendingCurationChat,
  usePendingCurationChats,
} from "@/features/data-curation/hooks/usePendingCurationChats";
import {
  datasetDisplayStatus,
  statusLabel,
  statusVariant,
} from "@/features/data-curation/status";
import type { CurationCellStatus } from "@/features/data-curation/types";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  PanelRightOpen,
  Plus,
  Trash2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const NAV_HEIGHT = "3.5rem"; // h-14, matches the AppShell NavBar

const terminalRunStatuses = new Set([
  "completed",
  "partial",
  "failed",
  "cancelled",
]);

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const PendingCurationCard = ({
  chatOpen,
  deleting,
  staged,
  onShowChat,
  onDelete,
}: {
  chatOpen: boolean;
  deleting: boolean;
  /** The stages above show the work, so the card shows only its actions. */
  staged: boolean;
  onShowChat: () => void;
  onDelete: () => void;
}) => (
  <Card className="border-dashed">
    <CardContent
      className={cn(
        "flex flex-col items-center text-center",
        staged ? "py-4 sm:flex-row sm:justify-between sm:text-left" : "py-20",
      )}
    >
      {staged ? (
        <p className="text-sm text-zinc-600">
          The dataset appears here when the agent creates it.
        </p>
      ) : (
        <div role="status" className="flex flex-col items-center">
          <Loader2 className="h-6 w-6 motion-safe:animate-spin text-zinc-400" aria-hidden />
          <p className="mt-3 text-sm font-medium text-zinc-700">
            Setting up your dataset
          </p>
          <p className="mt-1 max-w-md text-sm text-zinc-500">
            The agent is working out the rows and columns. When it proposes them,
            they appear here for your review.
          </p>
        </div>
      )}
      <div className={cn("flex flex-wrap justify-center gap-2", staged ? "mt-3 sm:mt-0" : "mt-4")}>
        {!chatOpen && (
          <Button size="sm" variant="outline" onClick={onShowChat}>
            Show chat
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          loading={deleting}
          aria-label="Delete this new dataset"
          title="Delete this new dataset and its chat"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
          Delete
        </Button>
      </div>
    </CardContent>
  </Card>
);

/**
 * The old Datasets (the curator). They are deprecated: `archived` shows an
 * account's old datasets read-only, for accounts that have the new Datasets.
 */
export const DataCurationPage = ({ archived = false }: { archived?: boolean } = {}) => {
  useDocumentTitle(archived ? "Old datasets" : "Datasets");

  const [searchParams, setSearchParams] = useSearchParams();
  const datasetId = searchParams.get("dataset");
  // A new-curation chat whose curation does not exist yet.
  const pendingChatId = archived ? null : searchParams.get("chat");
  const setView = useCallback(
    (
      view: { dataset?: string | null; chat?: string | null },
      { replace = false }: { replace?: boolean } = {},
    ) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("dataset");
          next.delete("chat");
          if (view.dataset) next.set("dataset", view.dataset);
          if (view.chat) next.set("chat", view.chat);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );
  const setDatasetId = useCallback(
    (id: string | null, options?: { replace?: boolean }) =>
      setView({ dataset: id }, options),
    [setView],
  );
  // With nothing selected the page is the composer, like a new Paperclip chat.
  const composerView = !datasetId && !pendingChatId;
  const { pending, addPending, removePending } = usePendingCurationChats();
  const { deletePendingChat: deletePendingCuration, deleting: deletingPendingChat } =
    useDeletePendingCurationChat();
  const {
    open: chatOpen,
    setOpen: setChatOpen,
    width: chatWidth,
    setWidth: setChatWidth,
  } = useCurationChatPanel();
  const [page, setPage] = useState(1);
  const [details, setDetails] = useState<{ rowId: string; columnId: string | null } | null>(null);
  useEffect(() => setDetails(null), [datasetId]);
  const [downloadPending, setDownloadPending] = useState(false);
  const [imagesDownloadPending, setImagesDownloadPending] = useState(false);
  const [checkpointDownloadId, setCheckpointDownloadId] = useState<string | null>(
    null,
  );
  const [checkpointImagesDownloadId, setCheckpointImagesDownloadId] = useState<
    string | null
  >(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CurationCellStatus | "">("");
  const [columnFilter, setColumnFilter] = useState("");
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [columnToEdit, setColumnToEdit] = useState<string | null>(null);
  const [monitorDialog, setMonitorDialog] = useState<{
    open: boolean;
    scope: MonitorScope | null;
  }>({ open: false, scope: null });

  const datasetsQuery = useExtractionDatasets({
    refetchInterval: pending.length ? 2_000 : 5_000,
  });
  const datasetQuery = useExtractionDataset(datasetId);
  const dataset = datasetQuery.data;
  const progressQuery = useDatasetBuilderProgress({
    sessionId: pendingChatId,
    datasetId,
  });
  const filteredColumnGone =
    columnFilter !== "" &&
    dataset != null &&
    !dataset.columns.some((column) => column.column_id === columnFilter);
  useEffect(() => {
    if (filteredColumnGone) setColumnFilter("");
  }, [filteredColumnGone]);
  // The list knows the status already, so the rows load with the dataset, not after it.
  const datasetStatus =
    dataset?.status ?? datasetsQuery.data?.find((item) => item.id === datasetId)?.status;
  const rowsQuery = useExtractionRows(datasetId, datasetStatus, page);
  const gridQuery = useExtractionGrid(datasetId, datasetStatus);
  const checkpointsQuery = useExtractionCheckpoints(datasetId);
  const inspectFilters = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(columnFilter ? { column_id: columnFilter } : {}),
    }),
    [columnFilter, statusFilter],
  );
  const filtersActive = statusFilter !== "" || columnFilter !== "";
  const rowsPage = rowsQuery.data;
  // The filters run here at once. Only a status filter of a dataset with more rows than
  // one page also asks the server, which finds the matching rows of the other pages.
  const allRowsLoaded = rowsPage != null && rowsPage.rows.length >= rowsPage.total;
  const localRows = useMemo(
    () => rowsPage && filterGridRows(rowsPage.rows, statusFilter, columnFilter),
    [columnFilter, rowsPage, statusFilter],
  );
  const inspectQuery = useExtractionInspect(
    datasetId,
    datasetStatus,
    inspectFilters,
    statusFilter !== "" && !allRowsLoaded,
  );
  const deleteMutation = useDeleteExtractionDataset();
  // The curation's chat agent is working whenever it is running, whether
  // this tab streams it or it continues on the server.
  const curationChatId = dataset?.chat_session_id ?? undefined;
  const curationChatStreaming = useIsSessionStreaming(curationChatId);
  const curationChatStatus = useSessionStatus(
    curationChatId,
    !curationChatStreaming,
  );
  const agentWorking =
    curationChatStreaming ||
    curationChatStatus.data?.status === "running" ||
    Boolean(dataset?.agent_running);
  // Before the person starts the collection, the agent only answers in the chat.
  const agentCollecting =
    dataset?.status === "active" && !dataset.collection_waiting && agentWorking;
  const chatComposer = useRef<ChatComposerHandle | null>(null);
  useDeliverChatNotes(datasetId ?? null, curationChatId, agentWorking);
  const queryClient = useQueryClient();
  const refreshMonitors = useRefreshDatasetMonitors(datasetId ?? null);
  const serverAgentRunning = Boolean(dataset?.agent_running);
  // A monitor's run starts on the server, not in this tab. Its status is then
  // polled, so that the chat shows the run's messages when it ends.
  useEffect(() => {
    refreshMonitors();
    if (!serverAgentRunning || !curationChatId || curationChatStreaming) return;
    void queryClient.invalidateQueries({
      queryKey: queryKeys.sessionStatus(curationChatId),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.messages(curationChatId) });
  }, [curationChatId, curationChatStreaming, queryClient, refreshMonitors, serverAgentRunning]);
  const createCheckpointMutation = useCreateExtractionCheckpoint(datasetId);
  const restoreCheckpointMutation = useRestoreExtractionCheckpoint(datasetId);

  useEffect(() => {
    setPage(1);
    setDownloadError(null);
    setStatusFilter("");
    setColumnFilter("");
    setMonitorDialog({ open: false, scope: null });
    setColumnToEdit(null);
  }, [datasetId]);

  // A dataset created a moment ago is not yet in a list fetched before it, so the
  // list alone does not close it: only a dataset that no longer loads.
  const datasetGone = datasetQuery.isError || (datasetQuery.isSuccess && !datasetQuery.data);
  useEffect(() => {
    if (
      datasetId &&
      datasetGone &&
      datasetsQuery.data &&
      !datasetsQuery.data.some((item) => item.id === datasetId)
    ) {
      setDatasetId(null, { replace: true });
    }
  }, [datasetGone, datasetId, datasetsQuery.data, setDatasetId]);

  // The server attaches a new chat to the curation its agent creates; once the
  // list shows that, the chat moves from "setting up" to its curation.
  useEffect(() => {
    const datasets = datasetsQuery.data;
    if (!datasets) return;
    const curationForChat = new Map(
      datasets.flatMap((item) =>
        item.chat_session_id ? [[item.chat_session_id, item.id] as const] : [],
      ),
    );
    const resolved = pending
      .map((chat) => chat.sessionId)
      .filter((sessionId) => curationForChat.has(sessionId));
    if (resolved.length) removePending(resolved);
    const created = pendingChatId ? curationForChat.get(pendingChatId) : undefined;
    if (created) setView({ dataset: created }, { replace: true });
  }, [datasetsQuery.data, pending, pendingChatId, removePending, setView]);

  const startCuration = () => setView({});

  const deletePendingChat = () => {
    if (
      !pendingChatId ||
      !window.confirm(
        "Delete this new dataset and its chat? It has no dataset yet, so " +
          "nothing else is removed.",
      )
    ) {
      return;
    }
    void deletePendingCuration(pendingChatId, () => setView({}, { replace: true }));
  };

  const handleNewChatStarted = useCallback(
    (sessionId: string) => {
      addPending(sessionId);
      setView({ chat: sessionId });
      setChatOpen(true);
    },
    [addPending, setView, setChatOpen],
  );

  const hideChat = useCallback(() => setChatOpen(false), [setChatOpen]);

  const [chatRequest, setChatRequest] = useState<
    (ChatRequest & { datasetId: string }) | null
  >(null);
  const clearCellsMutation = useClearExtractionCells(datasetId ?? null);
  const clearCells = clearCellsMutation.mutateAsync;
  const addGridNote = useAddGridChatNote(datasetId ?? null);
  const handleCellAction = useCallback(
    async (action: CellAction, cells: SelectedGridCell[]) => {
      if (!datasetId) return;
      const filled = cells.flatMap((cell) =>
        cell.revision != null
          ? [{ rowId: cell.rowId, columnId: cell.columnId, revision: cell.revision }]
          : [],
      );
      let batchId: string | null = null;
      if (action !== "chat" && filled.length > 0) {
        try {
          batchId = await clearCells(filled);
        } catch (error) {
          toast.error(
            `Could not clear the cells: ${errorMessage(error, "unknown error")}. Nothing was changed.`,
          );
          return;
        }
      }
      if (action === "clear") {
        // A Retry asks the agent about the cells in its own message.
        if (batchId && curationChatId) void addGridNote(batchId);
        toast.success(
          `Cleared ${filled.length} ${filled.length === 1 ? "cell" : "cells"}.${
            batchId && curationChatId ? " A note in the chat tells the agent." : ""
          } To get the old values back, restore a checkpoint.`,
        );
        return;
      }
      setChatRequest((current) => ({
        id: (current?.id ?? 0) + 1,
        datasetId,
        ...(action === "retry"
          ? { text: retryMessage(datasetId, cells), send: true }
          : { cells }),
      }));
      setChatOpen(true);
    },
    [addGridNote, clearCells, curationChatId, datasetId, setChatOpen],
  );
  const handleChatRequestHandled = useCallback(
    (id: number) =>
      setChatRequest((current) => (current?.id === id ? null : current)),
    [],
  );
  const sendMonitorRequest = useCallback(
    (text: string) => {
      if (!datasetId) return;
      setChatRequest((current) => ({ id: (current?.id ?? 0) + 1, datasetId, text, send: true }));
      setChatOpen(true);
    },
    [datasetId, setChatOpen],
  );

  const orderedColumns = useMemo(
    () => [...(dataset?.columns ?? [])].sort((a, b) => a.position - b.position),
    [dataset?.columns],
  );
  const collecting =
    agentCollecting ||
    Boolean(
      gridQuery.data?.operation &&
        !terminalRunStatuses.has(gridQuery.data.operation.status),
    );
  const collectionNote = collecting
    ? "The agent is actively collecting data. Rows and verification update automatically."
    : (dataset?.checkpoint_count ?? 0) > 0
      ? ""
      : "This dataset is editable and ready for collection.";
  const hasImageColumns = dataset?.columns.some(
    (column) => ["image", "figure"].includes(column.value_type),
  ) ?? false;

  const summary = gridQuery.data?.summary ?? rowsQuery.data?.summary;
  // A stable array, so that the rows of the grid can skip renders.
  const displayedColumns = useMemo(
    () =>
      columnFilter
        ? orderedColumns.filter((column) => column.column_id === columnFilter)
        : orderedColumns,
    [columnFilter, orderedColumns],
  );
  const inspectedRows =
    statusFilter !== "" && !allRowsLoaded && !inspectQuery.isPlaceholderData
      ? inspectQuery.data?.rows
      : undefined;
  const serverRows = useMemo(
    () =>
      inspectedRows &&
      [...inspectedRows].sort(
        (left, right) => (left.row_number ?? 0) - (right.row_number ?? 0),
      ),
    [inspectedRows],
  );
  // Until the server's rows of a large dataset arrive, the grid shows the matching rows
  // of this page.
  const displayedRows = filtersActive ? (serverRows ?? localRows) : rowsPage?.rows;
  const rowsPending = rowsQuery.isPending && !serverRows;
  const rowsError = rowsQuery.error ?? (filtersActive && !localRows ? inspectQuery.error : null);
  // A column alone keeps every row, so the pages stay and all rows of the dataset match.
  const matchingRows = !filtersActive
    ? null
    : statusFilter === ""
      ? (rowsPage?.total ?? null)
      : serverRows
        ? serverRows.length
        : allRowsLoaded
          ? (localRows?.length ?? null)
          : null;
  const clearFilters = () => {
    setStatusFilter("");
    setColumnFilter("");
  };
  const totalPages = Math.max(1, Math.ceil((rowsQuery.data?.total ?? 0) / 200));
  usePrefetchExtractionRows(datasetId, page < totalPages ? page + 1 : null);
  usePrefetchRecentDatasets(datasetsQuery.data, datasetId, !datasetId || rowsPage != null);

  const deleteDataset = () => {
    if (
      !dataset ||
      !window.confirm(
        `Permanently delete “${dataset.name}” and its evidence repository? ` +
          "This cannot be undone.",
      )
    ) {
      return;
    }
    deleteMutation.mutate(dataset.id, {
      onSuccess: () => setView({}, { replace: true }),
    });
  };

  const downloadCsv = async () => {
    if (!dataset) return;
    setDownloadPending(true);
    setDownloadError(null);
    try {
      const result = await downloadExtractionCsv(dataset.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        result.filename ??
        `${dataset.name.replace(/[^\w.-]+/g, "_") || "extraction"}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(errorMessage(error, "Could not download the CSV."));
    } finally {
      setDownloadPending(false);
    }
  };

  const downloadImages = async () => {
    if (!dataset) return;
    setImagesDownloadPending(true);
    setDownloadError(null);
    try {
      const result = await downloadExtractionImages(dataset.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        result.filename ??
        `${dataset.name.replace(/[^\w.-]+/g, "_") || "extraction"}-images.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(
        errorMessage(error, "Could not download the image archive."),
      );
    } finally {
      setImagesDownloadPending(false);
    }
  };

  const saveCheckpoint = () => {
    if (!dataset) return;
    createCheckpointMutation.mutate(
      {
        expectedRevision: dataset.revision,
        message: `Saved from Datasets at ${new Date().toLocaleString()}`,
      },
      {
        onSuccess: (checkpoint) =>
          toast.success(`Saved checkpoint version ${checkpoint.sequence}`),
      },
    );
  };

  const downloadCheckpoint = async (
    checkpointId: string,
    fallbackName: string,
  ) => {
    if (!dataset) return;
    setCheckpointDownloadId(checkpointId);
    setDownloadError(null);
    try {
      const result = await downloadExtractionCheckpointCsv(
        dataset.id,
        checkpointId,
      );
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename ?? fallbackName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(
        errorMessage(error, "Could not download the checkpoint CSV."),
      );
    } finally {
      setCheckpointDownloadId(null);
    }
  };

  const downloadCheckpointImages = async (
    checkpointId: string,
    fallbackName: string,
  ) => {
    if (!dataset) return;
    setCheckpointImagesDownloadId(checkpointId);
    setDownloadError(null);
    try {
      const result = await downloadExtractionCheckpointImages(
        dataset.id,
        checkpointId,
      );
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename ?? fallbackName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(
        errorMessage(error, "Could not download the checkpoint image archive."),
      );
    } finally {
      setCheckpointImagesDownloadId(null);
    }
  };

  const restoreCheckpoint = (checkpointId: string, sequence: number) => {
    if (!dataset) return;
    if (
      !window.confirm(
        `Restore checkpoint v${sequence}? Current grid changes that were not ` +
          "saved as another checkpoint will be replaced.",
      )
    ) {
      return;
    }
    restoreCheckpointMutation.mutate({
      checkpointId,
      expectedRevision: dataset.revision,
    });
  };

  const actionError =
    createCheckpointMutation.error ??
    restoreCheckpointMutation.error ??
    deleteMutation.error;
  const conflictAction =
    actionError instanceof ExtractionRequestError && actionError.status === 409;

  const fast = Boolean(dataset?.builder);
  const editable = dataset?.status === "active" && !archived;
  const detailsRow = details && displayedRows?.find((row) => row.id === details.rowId);
  const detailsPanel = (
    <SlideablePanel
      id="curation-details-panel"
      isOpen={Boolean(detailsRow)}
      onClose={() => setDetails(null)}
      offsetTop={NAV_HEIGHT}
      from="right"
      width={`${chatWidth}px`}
      alwaysOpenOnDesktop={false}
      ariaLabel="Row details"
      resizability={{
        onWidthChange: setChatWidth,
        minWidth: CURATION_CHAT_MIN_WIDTH,
        maxWidth: CURATION_CHAT_MAX_WIDTH,
      }}
    >
      {detailsRow && dataset && (
        <FastDetailsPanel
          datasetId={dataset.id}
          row={detailsRow}
          columns={orderedColumns}
          focusColumnId={details.columnId}
          onFocusColumn={(columnId) => setDetails({ rowId: detailsRow.id, columnId })}
          onClose={() => setDetails(null)}
        />
      )}
    </SlideablePanel>
  );

  const chat = (
    <SlideablePanel
      id="curation-chat-panel"
      isOpen={chatOpen}
      onClose={hideChat}
      offsetTop={NAV_HEIGHT}
      from="right"
      width={`${chatWidth}px`}
      alwaysOpenOnDesktop={false}
      ariaLabel="Datasets chat"
      resizability={{
        onWidthChange: setChatWidth,
        minWidth: CURATION_CHAT_MIN_WIDTH,
        maxWidth: CURATION_CHAT_MAX_WIDTH,
      }}
    >
      {pendingChatId ? (
        <CurationChatPanel pendingSessionId={pendingChatId} onHide={hideChat} />
      ) : datasetId && dataset ? (
        <CurationChatPanel
          dataset={dataset}
          agentWorking={agentWorking}
          request={chatRequest?.datasetId === dataset.id ? chatRequest : null}
          onRequestHandled={handleChatRequestHandled}
          composerRef={chatComposer}
          onHide={hideChat}
        />
      ) : (
        <CurationChatPlaceholder onStart={startCuration} onHide={hideChat} />
      )}
    </SlideablePanel>
  );

  return (
    <CurationWorkspaceLayout
      chat={composerView ? null : fast ? detailsPanel : archived ? null : chat}
    >
      <main className="flex w-full flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <OldDatasetsBanner archived={archived} />
        {(!composerView || archived) && (
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-display text-2xl leading-none tracking-tight text-zinc-900">
              {archived ? "Old datasets" : "Datasets"}
            </h1>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {!archived && (
                <Button size="sm" variant="outline" onClick={startCuration}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Start a dataset
                </Button>
              )}
              {!fast && !archived && (
                <Button
                  size="sm"
                  variant="outline"
                  aria-controls="curation-chat-panel"
                  aria-expanded={chatOpen}
                  onClick={() => setChatOpen(!chatOpen)}
                >
                  <PanelRightOpen className="h-4 w-4" aria-hidden />
                  {chatOpen ? "Hide chat" : "Show chat"}
                </Button>
              )}
            </div>
          </header>
        )}

        {datasetsQuery.isLoading && !composerView ? (
          <Card>
            <CardContent className="py-20 text-center text-sm text-zinc-500" role="status">
              Loading extraction datasets…
            </CardContent>
          </Card>
        ) : datasetsQuery.error && !composerView ? (
          <p
            role="alert"
            className="rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            {errorMessage(datasetsQuery.error, "Could not load extraction datasets.")}
          </p>
        ) : (
          <section className="min-w-0" aria-label="Selected extraction">
            {composerView && archived ? (
              <Card>
                <CardContent className="py-20 text-center text-sm text-zinc-500">
                  {datasetsQuery.isLoading
                    ? "Loading your old datasets…"
                    : datasetsQuery.data?.length
                      ? "Open one of your old datasets from the list."
                      : "You have no old datasets."}
                </CardContent>
              </Card>
            ) : composerView ? (
              <CurationComposer
                onStarted={handleNewChatStarted}
                onFastStarted={(id) => {
                  void datasetsQuery.refetch();
                  setDatasetId(id);
                }}
              />
            ) : pendingChatId ? (
              <div className="space-y-3">
                <ComprehensiveProgress progress={progressQuery.data} datasetId={null} />
                <PendingCurationCard
                  chatOpen={chatOpen}
                  deleting={deletingPendingChat}
                  staged={Boolean(progressQuery.data?.stages.length)}
                  onShowChat={() => setChatOpen(true)}
                  onDelete={deletePendingChat}
                />
              </div>
            ) : datasetQuery.isLoading ? (
              <Card>
                <CardContent className="py-20 text-center text-sm text-zinc-500" role="status">
                  Loading extraction…
                </CardContent>
              </Card>
            ) : datasetQuery.error || !dataset ? (
              <p
                role="alert"
                className="rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              >
                {errorMessage(datasetQuery.error, "Could not load this extraction.")}
              </p>
            ) : (
              <div className="space-y-3">
                <CurationHeader
                  dataset={dataset}
                  status={{
                    label: statusLabel(datasetDisplayStatus(dataset, collecting)),
                    variant: statusVariant(datasetDisplayStatus(dataset, collecting)),
                  }}
                  agentTime={
                    dataset.agent_seconds == null
                      ? null
                      : {
                          seconds: dataset.agent_seconds,
                          running: agentWorking,
                          measuredAt: datasetQuery.dataUpdatedAt,
                        }
                  }
                  agentWorking={agentWorking}
                  hasImageColumns={hasImageColumns}
                  iconsOnly={fast ? Boolean(detailsRow) : chatOpen && !archived}
                  plan={gridQuery.data?.plan}
                  activity={gridQuery.data?.activity}
                  checkpoints={checkpointsQuery.data}
                  checkpointsLoading={checkpointsQuery.isPending}
                  checkpointsError={
                    checkpointsQuery.error
                      ? errorMessage(checkpointsQuery.error, "Could not load checkpoints.")
                      : null
                  }
                  savingCheckpoint={createCheckpointMutation.isPending}
                  deleting={deleteMutation.isPending}
                  csvPending={downloadPending}
                  imagesPending={imagesDownloadPending}
                  checkpointCsvId={checkpointDownloadId}
                  checkpointImagesId={checkpointImagesDownloadId}
                  restoringCheckpointId={
                    restoreCheckpointMutation.isPending
                      ? (restoreCheckpointMutation.variables?.checkpointId ?? null)
                      : null
                  }
                  onSaveCheckpoint={saveCheckpoint}
                  onDownloadCsv={() => void downloadCsv()}
                  onDownloadImages={() => void downloadImages()}
                  onDelete={deleteDataset}
                  onDownloadCheckpointCsv={(checkpoint) =>
                    void downloadCheckpoint(
                      checkpoint.id,
                      `${dataset.name}-v${checkpoint.sequence}.csv`,
                    )
                  }
                  onDownloadCheckpointImages={(checkpoint) =>
                    void downloadCheckpointImages(
                      checkpoint.id,
                      `${dataset.name}-v${checkpoint.sequence}-images.zip`,
                    )
                  }
                  onRestoreCheckpoint={(checkpoint) =>
                    restoreCheckpoint(checkpoint.id, checkpoint.sequence)
                  }
                  onNewMonitor={
                    editable
                      ? () => setMonitorDialog({ open: true, scope: null })
                      : null
                  }
                />
                <ComprehensiveProgress progress={progressQuery.data} datasetId={dataset.id} />
                <FastQueryLine dataset={dataset} />
                {dataset.status !== "draft" && <MonitorsPanel datasetId={dataset.id} />}
                {editable && (
                  <MonitorDialog
                    datasetId={dataset.id}
                    columns={orderedColumns}
                    open={monitorDialog.open}
                    scope={monitorDialog.scope}
                    agentWorking={agentWorking}
                    onClose={() =>
                      setMonitorDialog((current) => ({ ...current, open: false }))
                    }
                    onSend={sendMonitorRequest}
                  />
                )}

                {actionError && (
                  <div
                    role="alert"
                    className="flex flex-col gap-3 rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span>{errorMessage(actionError, "The action could not be completed.")}</span>
                    {conflictAction && (
                      <Button
                        variant="outline"
                        size="sm"
                        loading={datasetQuery.isFetching}
                        onClick={() => {
                          void datasetQuery.refetch();
                          void rowsQuery.refetch();
                        }}
                      >
                        Reload latest
                      </Button>
                    )}
                  </div>
                )}
                {downloadError && (
                  <p
                    role="alert"
                    className="rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                  >
                    {downloadError}
                  </p>
                )}

                {dataset.status === "draft" && archived ? (
                  <Card>
                    <CardContent className="py-20 text-center text-sm text-zinc-500">
                      This dataset was never built: its columns were not approved.
                    </CardContent>
                  </Card>
                ) : dataset.status === "draft" ? (
                  <ProposedSchemaCard
                    key={dataset.id}
                    dataset={dataset}
                    agentWorking={agentWorking}
                    fullRead={fullReadOf(progressQuery.data)}
                    composer={chatComposer}
                  />
                ) : rowsPending ? (
                  <Card>
                    <CardContent className="py-20 text-center text-sm text-zinc-500" role="status">
                      Loading extracted rows…
                    </CardContent>
                  </Card>
                ) : rowsError || !displayedRows || !summary ? (
                  <div
                    role="alert"
                    className="flex flex-col gap-3 rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span>{errorMessage(rowsError, "Could not load extracted rows.")}</span>
                    {filtersActive && (
                      <Button variant="outline" size="sm" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {editable &&
                      (dataset.collection_waiting && !collecting ? (
                        <StartCollectionBar
                          dataset={dataset}
                          agentWorking={agentWorking}
                          composer={chatComposer}
                          onStarted={() => setChatOpen(true)}
                        />
                      ) : (
                        <p
                          className={collectionNote ? "text-xs leading-5 text-zinc-500" : "sr-only"}
                          aria-live="polite"
                        >
                          {collectionNote}
                        </p>
                      ))}
                    <CurationRowLimitBanner
                      rowLimit={dataset.row_limit}
                      totalRows={summary.total_rows}
                      estimate={dataset.corpus_estimate}
                    />
                    <div id="curation-grid" className="scroll-mt-4">
                      {columnFilter && (
                        <div
                          role="status"
                          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-[3px] border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
                        >
                          <span>
                            Showing only the column{" "}
                            <span className="font-semibold">
                              {orderedColumns.find(
                                (column) => column.column_id === columnFilter,
                              )?.label ?? "selected"}
                            </span>
                            .
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setColumnFilter("")}
                          >
                            Show all columns
                          </Button>
                        </div>
                      )}
                      <CurationTable
                        datasetId={dataset.id}
                        onFocusCell={
                          fast ? (rowId, columnId) => setDetails({ rowId, columnId }) : undefined
                        }
                        chatActions={!fast && !archived}
                        repoId={dataset.repo_id}
                        columns={displayedColumns}
                        columnStats={gridQuery.data?.columns ?? []}
                        rows={displayedRows}
                        summary={summary}
                        filtered={filtersActive}
                        collecting={
                          collecting &&
                          !filtersActive &&
                          displayedRows.length === 0
                        }
                        collectionStartedAt={
                          gridQuery.data?.plan?.created_at ?? null
                        }
                        collectionSummary={
                          gridQuery.data?.plan?.summary ?? null
                        }
                        rowLimit={
                          dataset.row_limit &&
                          summary.total_rows >= dataset.row_limit &&
                          !filtersActive &&
                          page >= totalPages
                            ? {
                                limit: dataset.row_limit,
                                estimate: dataset.corpus_estimate,
                              }
                            : null
                        }
                        canDeleteRows={editable}
                        columnEdits={
                          editable
                            ? {
                                revision: dataset.revision,
                                columnCount: dataset.columns.length,
                                agentWorking,
                                onAdd: () => setAddColumnOpen(true),
                                onEdit: (column) => setColumnToEdit(column.column_id),
                              }
                            : null
                        }
                        onFocusColumn={setColumnFilter}
                        onCellAction={
                          archived
                            ? undefined
                            : (action, cells) => void handleCellAction(action, cells)
                        }
                        cellActionPending={clearCellsMutation.isPending}
                        monitor={
                          editable
                            ? {
                                rowsFiltered: statusFilter !== "",
                                columnsFiltered: columnFilter !== "",
                                onOpen: (scope) => setMonitorDialog({ open: true, scope }),
                              }
                            : null
                        }
                        filtersActive={filtersActive}
                        filters={
                          <GridFilters
                            status={statusFilter}
                            columnId={columnFilter}
                            columns={orderedColumns}
                            matching={matchingRows}
                            onStatusChange={setStatusFilter}
                            onColumnChange={setColumnFilter}
                            onClear={clearFilters}
                          />
                        }
                      />
                      {editable && (
                        <>
                          <AddColumnDialog
                            dataset={dataset}
                            open={addColumnOpen}
                            agentWorking={agentWorking}
                            onClose={() => setAddColumnOpen(false)}
                          />
                          <EditColumnDialog
                            dataset={dataset}
                            column={
                              dataset.columns.find(
                                (column) => column.column_id === columnToEdit,
                              ) ?? null
                            }
                            agentWorking={agentWorking}
                            onClose={() => setColumnToEdit(null)}
                          />
                        </>
                      )}
                    </div>
                    {statusFilter === "" && totalPages > 1 && (
                      <nav
                        className="flex items-center justify-end gap-2"
                        aria-label="Extraction rows pages"
                      >
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Previous rows page"
                          disabled={page <= 1}
                          onClick={() => setPage((current) => current - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" aria-hidden />
                        </Button>
                        <span className="text-xs tabular-nums text-zinc-500">
                          Page {page} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Next rows page"
                          disabled={page >= totalPages}
                          onClick={() => setPage((current) => current + 1)}
                        >
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </Button>
                      </nav>
                    )}
                    <CurationIncoming
                      papers={gridQuery.data?.incoming ?? []}
                      total={gridQuery.data?.incoming_total ?? 0}
                    />
                  </>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </CurationWorkspaceLayout>
  );
};
